import { queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";

export const PERFORMANCE_REVIEWS_KEYS = {
	all: ["performance-reviews"] as const,
	inbox: () => [...PERFORMANCE_REVIEWS_KEYS.all, "inbox"] as const,
	detail: (review_id: string) =>
		[...PERFORMANCE_REVIEWS_KEYS.all, review_id] as const,
};

const ReviewIdSchema = z.object({
	review_id: z.string().min(1),
});

const fetchInboxReviews = createServerFn({ method: "GET" }).handler(
	async () => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api["performance-reviews"].$get();

		if (!result.ok) {
			throw new Error("Failed to fetch performance reviews");
		}

		return await result.json();
	},
);

const fetchReview = createServerFn({ method: "GET" })
	.validator(ReviewIdSchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api["performance-reviews"][
			":review_id"
		].$get({
			param: { review_id: data.review_id },
		});

		if (!result.ok) {
			// The route types only declare 200; widen before the 404 check.
			const status: number = result.status;
			if (status === 404) {
				throw notFound();
			}
			throw new Error("Failed to fetch performance review");
		}

		return await result.json();
	});

export const inboxReviewsQueryOptions = queryOptions({
	queryKey: PERFORMANCE_REVIEWS_KEYS.inbox(),
	queryFn: () => fetchInboxReviews(),
});

export const reviewDetailQueryOptions = (review_id: string) =>
	queryOptions({
		queryKey: PERFORMANCE_REVIEWS_KEYS.detail(review_id),
		queryFn: () => fetchReview({ data: { review_id } }),
	});

const InputSummaryRequestSchema = ReviewIdSchema.extend({
	language: z.enum(["en", "nl", "fr"]),
});

const fetchInputSummary = createServerFn({ method: "GET" })
	.validator(InputSummaryRequestSchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api["performance-reviews"][
			":review_id"
		]["input-summary"].$get({
			param: { review_id: data.review_id },
			query: { language: data.language },
		});
		if (!result.ok) {
			throw new Error("Failed to fetch the feedback summary");
		}
		return await result.json();
	});

/** How often to look again while the summary is still being made. */
const INPUT_SUMMARY_POLL_MS = 3000;

export const inputSummaryQueryOptions = (
	review_id: string,
	language: "en" | "nl" | "fr",
) =>
	queryOptions({
		queryKey: [
			...PERFORMANCE_REVIEWS_KEYS.detail(review_id),
			"input-summary",
			language,
		],
		queryFn: () => fetchInputSummary({ data: { review_id, language } }),
		refetchInterval: (query) =>
			query.state.data?.status === "pending" ? INPUT_SUMMARY_POLL_MS : false,
	});

export type InputSummary = Awaited<ReturnType<typeof fetchInputSummary>>;

export type InboxReview = Awaited<ReturnType<typeof fetchInboxReviews>>[number];
export type ReviewDetail = Awaited<ReturnType<typeof fetchReview>>;
export type ReviewContribution = ReviewDetail["contributions"][number];
export type ReviewPart = ReviewDetail["parts"][number];
export type ReviewQuestion = ReviewPart["questions"][number];
export type ReviewGoal = ReviewDetail["goals"][number];
